import React from "react";
import { Box, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import { useData } from "../providers/DataProvider";

const StyledIconButton = styled(IconButton)(({ theme, position }) => ({
  backgroundColor: theme.palette.background.inverted,
  color: theme.palette.text.inverted,
  border: "1px solid",
  borderColor: theme.palette.text.inverted,
  padding: "4px",
  position: "absolute",
  top: "0",
  left: position === "left" ? "0" : "",
  right: position === "right" ? "0" : "",
  transform: `translate(${position === "left" ? "-100%" : "100%"}, -100%)`,
  "&:hover": {
    backgroundColor: theme.palette.background.inverted,
  },
  "& > .MuiSvgIcon-root": {
    fontSize: "16px",
  },
}));

const StyledPatchPointsBadge = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: "0px",
  right: "0px",
  transform: "translate(100%, 100%)",
  backgroundColor: theme.palette.background.inverted,
  color: theme.palette.text.inverted,
  fontSize: "14px",
  fontWeight: "700",
  height: "26px",
  width: "26px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  paddingTop: "3px",
}));

export default function CustomizerCanvas({
  canvasImage,
  canvasZones,
  canvasPatches,
  handleRotatePatch,
  handleDeletePatch,
}) {
  const data = useData();
  const canvasRef = React.useRef();

  // Effects.

  React.useEffect(() => {
    const context = canvasRef.current.getContext("2d");

    // Draw zones.
    const production =
      process.env.NODE_ENV === "production" &&
      !process.env.REACT_APP_CALLBACK_URL.includes("staging");
    if (canvasZones.length) {
      for (const zone of canvasZones) {
        drawShape(
          context,
          zone.zone_geometry,
          zone.collision ? "red" : zone.droppable ? "green" : production ? "transparent" : "blue"
        );
      }
    }

    // FOR DEV ONLY - Draw patch outlines.
    // if (canvasPatches.filter((e) => e.position === canvasImage.view).length) {
    //   for (const patch of canvasPatches.filter((e) => e.position === canvasImage.view)) {
    //     drawShape(context, patch.coordinates, "yellow");
    //   }
    // }

    // Draw grid lines.
    // drawGridLines(context);

    // Clear canvas for next redraw.
    return () => {
      context.clearRect(
        0,
        0,
        canvasImage.width * canvasImage.zoom,
        canvasImage.height * canvasImage.zoom
      );
    };
  });

  // Drawing.

  // const drawLine = (context, x1, y1, x2, y2, color, dash) => {
  //   context.lineWidth = 1;
  //   context.strokeStyle = color ? color : "#000000";
  //   context.beginPath();
  //   context.setLineDash(dash ? dash : []);
  //   context.moveTo(x1, y1);
  //   context.lineTo(x2, y2);
  //   context.closePath();
  //   context.stroke();
  // };

  const drawShape = (context, coordinates, color) => {
    context.lineWidth = 3;
    context.lineJoin = "round";
    context.strokeStyle = color;
    context.beginPath();
    for (let i = 0; i < coordinates.length; i++) {
      const x = coordinates[i].x * canvasImage.zoom;
      const y = coordinates[i].y * canvasImage.zoom;
      i === 0 ? context.moveTo(x, y) : context.lineTo(x, y);
    }
    context.closePath();
    context.stroke();
  };

  // const drawGridLines = (context) => {
  //   const color = "#dddddd";
  //   const point = (i) => data.variant.ppi * i * canvasImage.zoom;
  //   const width = canvasImage.width;
  //   const height = canvasImage.height;
  //   const verLines = [...new Array(Math.floor(data.variant.width) + 1)];
  //   const horLines = [...new Array(Math.floor(data.variant.height) + 1)];
  //   horLines.forEach((e, i) => {
  //     if (i > 0) {
  //       const y = point(i);
  //       drawLine(context, 0, y, width, y, color, true);
  //     }
  //   });
  //   verLines.forEach((e, i) => {
  //     if (i > 0) {
  //       const x = point(i);
  //       drawLine(context, 0, x, height, x, color, true);
  //     }
  //   });
  // }

  // Render.

  return (
    <>
      <img
        src={canvasImage.src}
        width={canvasImage.width * canvasImage.zoom}
        height={canvasImage.height * canvasImage.zoom}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "none",
        }}
      />
      <canvas
        ref={canvasRef}
        width={canvasImage.width * canvasImage.zoom}
        height={canvasImage.height * canvasImage.zoom}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          userSelect: "none",
        }}
      ></canvas>
      {canvasPatches.map((patch, index) => (
        <div
          key={index}
          style={{ display: !patch.visible || patch.position !== canvasImage.view ? "none" : "" }}
        >
          <img
            className="draggable selectable"
            src={patch.src}
            style={{
              position: "absolute",
              top: patch.top * canvasImage.zoom,
              left: patch.left * canvasImage.zoom,
              width: patch.width * data.variant.ppi * canvasImage.zoom,
              height: patch.height * data.variant.ppi * canvasImage.zoom,
              transform: `rotate(${patch.rotation}deg)`,
              zIndex: patch.selected ? 4 : 2,
            }}
            data-patch={JSON.stringify({ ...patch, index })}
            draggable={false}
          />
          {patch.selected && (
            <div
              style={{
                position: "absolute",
                zIndex: patch.selected ? 3 : 1,
                top: patch.bounds.top * canvasImage.zoom,
                left: patch.bounds.left * canvasImage.zoom,
                width: patch.bounds.width * canvasImage.zoom,
                height: patch.bounds.height * canvasImage.zoom,
                outline: "2px dashed #eeeeee",
              }}
            >
              <StyledIconButton
                position="left"
                onClick={(event) => handleRotatePatch(event, patch, index)}
                onMouseUp={(event) => event.stopPropagation()}
                onTouchEnd={(event) => event.stopPropagation()}
              >
                <RotateRightIcon />
              </StyledIconButton>
              <StyledIconButton
                position="right"
                onClick={(event) => handleDeletePatch(event, index)}
                onMouseUp={(event) => event.stopPropagation()}
                onTouchEnd={(event) => event.stopPropagation()}
              >
                <DeleteIcon />
              </StyledIconButton>
              <StyledPatchPointsBadge>{patch.points}</StyledPatchPointsBadge>
            </div>
          )}
        </div>
      ))}
    </>
  );
}
